import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
}

const isoDate = (d) => d.toISOString().slice(0, 10);

export default function DoctorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [doctor, setDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const [windowStart, setWindowStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Booking flow state
  const [chosen, setChosen] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | booking | paying | success
  const [appointment, setAppointment] = useState(null);
  const [payment, setPayment] = useState(null);
  const [paymentRequired, setPaymentRequired] = useState(true);
  const [paymentMode, setPaymentMode] = useState("online");
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const days = useMemo(() => {
    const out = [];

    for (let i = 0; i < 14; i++) {
      const d = new Date(windowStart);
      d.setDate(windowStart.getDate() + i);
      out.push(d);
    }

    return out;
  }, [windowStart]);

  const refreshSlots = () => {
    const from = isoDate(days[0]);
    const to = isoDate(days[days.length - 1]);

    return api
      .get(`/patient/doctors/${id}/slots`, {
        params: { from, to },
      })
      .then((r) => setSlots(r.data.slots || []));
  };

  useEffect(() => {
    api
      .get(`/patient/doctors/${id}`)
      .then((r) => setDoctor(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    refreshSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, days]);

  const slotsByDate = useMemo(() => {
    const map = {};

    for (const s of slots) {
      const d = s.start.slice(0, 10);
      (map[d] ||= []).push(s);
    }

    return map;
  }, [slots]);

  const openBookingDialog = (slot) => {
    setChosen(slot);
    setStage("booking");
    setAppointment(null);
    setPayment(null);
    setPaymentRequired(true);
    setPaymentMode("online");
    setErrMsg("");
  };

  const startBooking = async () => {
    setSubmitting(true);
    setErrMsg("");

    try {
      const { data } = await api.post("/patient/appointments", {
        doctor_id: id,
        slot_start: chosen.start,
      });

      setAppointment(data.appointment);
      setPayment(data.payment || null);

      const requiresPayment =
        data.payment_required !== false &&
        data.payment_mode !== "offline";

      setPaymentRequired(requiresPayment);
      setPaymentMode(data.payment_mode || (requiresPayment ? "online" : "offline"));

      // Hospital has online payments OFF:
      // appointment is already confirmed by backend.
      if (!requiresPayment) {
        setStage("success");

        toast.success(
          "Appointment confirmed — Pay at Reception"
        );

        refreshSlots();
        return;
      }

      // Hospital has online payments ON:
      // continue to Razorpay.
      setStage("paying");
    } catch (e) {
      setErrMsg(apiError(e, "Booking failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPayment = async () => {
    if (!appointment?.id) return;

    setSubmitting(true);
    setErrMsg("");

    try {
      const { data } = await api.post(
        `/patient/appointments/${appointment.id}/pay`,
        {}
      );

      if (data.provider !== "razorpay") {
        throw new Error(
          `Unsupported payment provider: ${data.provider || "unknown"}`
        );
      }

      const loaded = await loadRazorpayScript();

      if (!loaded) {
        throw new Error("Unable to load Razorpay checkout");
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,

        name: "MediBooks",

        description: `Appointment with ${
          doctor?.full_name || "Doctor"
        }`,

        handler: async (response) => {
          try {
            const verify = await api.post(
              `/patient/appointments/${appointment.id}/verify-payment`,
              {
                provider: "razorpay",
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              }
            );

            setAppointment(verify.data.appointment);
            setPaymentRequired(true);
            setPaymentMode("online");
            setStage("success");
            setErrMsg("");

            toast.success(
              "Payment successful — appointment confirmed"
            );

            refreshSlots();
          } catch (verifyError) {
            setErrMsg(
              apiError(
                verifyError,
                "Payment verification failed"
              )
            );

            toast.error("Payment verification failed");
          }
        },

        modal: {
          ondismiss: () => {
            setErrMsg(
              "Payment was cancelled. You can try again."
            );
          },
        },

        theme: {
          color: "#14b8a6",
        },
      };

      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      setErrMsg(
        apiError(
          e,
          e?.message || "Payment error"
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStage("idle");
    setChosen(null);
    setAppointment(null);
    setPayment(null);
    setPaymentRequired(true);
    setPaymentMode("online");
    setErrMsg("");
  };

  return (
    <div className="space-y-8" data-testid="doctor-detail-page">
      <Link
        to={
          doctor?.hospital
            ? `/patient/hospitals/${doctor.hospital.id}`
            : "/patient/hospitals"
        }
        className="text-sm text-mb-fg2 hover:text-white inline-flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Link>

      {loading ? (
        <div className="text-sm text-mb-fg2">Loading…</div>
      ) : doctor ? (
        <>
          <div
            className="rounded-xl border border-mb-border bg-mb-surface p-6 sm:p-8 flex flex-col sm:flex-row gap-6 sm:items-center"
            data-testid="doctor-info"
          >
            <div className="h-20 w-20 rounded-full bg-teal-500/10 text-teal-300 flex items-center justify-center text-2xl font-light">
              {(doctor.full_name || "?")
                .split(" ")
                .map((s) => s[0])
                .slice(0, 2)
                .join("")}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">
                ◖ {doctor.specialty}
              </div>

              <h1 className="mt-1 text-3xl tracking-tight font-light truncate">
                {doctor.full_name}
              </h1>

              <div className="mt-2 text-sm text-mb-fg2">
                {doctor.hospital?.name}
              </div>

              {doctor.bio && (
                <p className="mt-3 text-sm text-mb-fg2 max-w-2xl">
                  {doctor.bio}
                </p>
              )}
            </div>

            <div className="text-right shrink-0">
              <div className="text-2xl text-white">
                {formatCurrency(
                  doctor.consultation_fee,
                  { decimals: false }
                )}
              </div>

              <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
                {doctor.slot_duration_minutes} min consultation
              </div>
            </div>
          </div>

          {/* Date range navigation */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-teal-400" />
              Choose a slot
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                data-testid="slots-prev"
                onClick={() => {
                  const d = new Date(windowStart);
                  d.setDate(windowStart.getDate() - 14);

                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  setWindowStart(
                    d < today ? today : d
                  );
                }}
                className="hover:bg-mb-surface2 h-9"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <Button
                variant="ghost"
                data-testid="slots-next"
                onClick={() => {
                  const d = new Date(windowStart);
                  d.setDate(windowStart.getDate() + 14);
                  setWindowStart(d);
                }}
                className="hover:bg-mb-surface2 h-9"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {days.map((d) => {
              const k = isoDate(d);
              const list = slotsByDate[k] || [];

              return (
                <div
                  key={k}
                  className="rounded-xl border border-mb-border bg-mb-surface"
                  data-testid={`day-block-${k}`}
                >
                  <div className="px-5 py-3 border-b border-mb-border flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-white font-medium">
                        {d.toLocaleDateString([], {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>

                      <span className="text-mb-fg2 ml-2 text-xs">
                        {list.length} slots
                      </span>
                    </div>
                  </div>

                  <div className="px-5 py-4">
                    {list.length === 0 ? (
                      <div className="text-xs text-zinc-600">
                        No slots available.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {list.map((s) => (
                          <button
                            key={s.start}
                            onClick={() =>
                              openBookingDialog(s)
                            }
                            data-testid={`slot-${s.start}`}
                            className="px-3 py-1.5 rounded-lg bg-mb-surface2 border border-mb-border text-xs text-zinc-200 hover:border-teal-400 hover:text-teal-300 transition-colors inline-flex items-center gap-1.5"
                          >
                            <Clock className="h-3 w-3" />

                            {new Date(
                              s.start
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div>Doctor not found.</div>
      )}

      {/* Booking dialog */}
      <Dialog
        open={stage !== "idle"}
        onOpenChange={(o) => !o && reset()}
      >
        <DialogContent className="bg-mb-surface border-mb-border text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light text-2xl">
              {stage === "booking" &&
                "Confirm booking"}

              {stage === "paying" &&
                "Secure payment"}

              {stage === "success" &&
                "Appointment confirmed"}
            </DialogTitle>

            <DialogDescription className="text-mb-fg2 text-sm">
              {stage === "booking" &&
                "Review the slot and confirm your appointment."}

              {stage === "paying" &&
                "Complete your payment securely to confirm the appointment."}

              {stage === "success" &&
                (paymentMode === "offline" ||
                paymentRequired === false
                  ? "Your appointment is confirmed. Please pay at the hospital reception."
                  : "Your payment was successful and the appointment is confirmed.")}
            </DialogDescription>
          </DialogHeader>

          {stage === "booking" &&
            chosen &&
            doctor && (
              <div className="space-y-4">
                <Row
                  k="Doctor"
                  v={doctor.full_name}
                />

                <Row
                  k="Specialty"
                  v={doctor.specialty}
                />

                <Row
                  k="Hospital"
                  v={doctor.hospital?.name}
                />

                <Row
                  k="Date & time"
                  v={new Date(
                    chosen.start
                  ).toLocaleString()}
                />

                <Row
                  k="Consultation fee"
                  v={formatCurrency(
                    doctor.consultation_fee
                  )}
                />

                {errMsg && (
                  <div
                    className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                    data-testid="booking-error"
                  >
                    {errMsg}
                  </div>
                )}

                <DialogFooter>
                  <Button
                    variant="ghost"
                    onClick={reset}
                    className="hover:bg-mb-surface2"
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={startBooking}
                    disabled={submitting}
                    data-testid="confirm-booking-button"
                    className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-medium"
                  >
                    {submitting
                      ? "Reserving…"
                      : "Reserve & continue"}
                  </Button>
                </DialogFooter>
              </div>
            )}

          {stage === "paying" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-mb-border bg-mb-surface2 p-5">
                <div className="flex items-center gap-2 text-sm text-white">
                  <CreditCard className="h-4 w-4 text-teal-400" />
                  Secure online payment
                </div>

                <div className="mt-3 text-sm text-mb-fg2">
                  You will be redirected to your
                  hospital&apos;s configured payment gateway.
                </div>

                <div className="mt-4 text-lg text-white">
                  {formatCurrency(
                    payment?.amount || 0
                  )}
                </div>
              </div>

              {errMsg && (
                <div
                  className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                  data-testid="payment-error"
                >
                  {errMsg}
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={reset}
                  className="hover:bg-mb-surface2"
                >
                  Cancel
                </Button>

                <Button
                  onClick={submitPayment}
                  disabled={submitting}
                  data-testid="pay-button"
                  className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-medium"
                >
                  {submitting
                    ? "Preparing payment…"
                    : `Pay ${formatCurrency(
                        payment?.amount || 0
                      )}`}
                </Button>
              </DialogFooter>
            </div>
          )}

          {stage === "success" &&
            appointment && (
              <div className="space-y-4 text-center">
                <CheckCircle2 className="h-12 w-12 text-teal-400 mx-auto" />

                <div className="text-sm text-mb-fg2">
                  Your appointment with{" "}
                  <span className="text-white">
                    {appointment.doctor?.full_name}
                  </span>{" "}
                  on{" "}
                  <span className="text-white">
                    {new Date(
                      appointment.slot_start
                    ).toLocaleString()}
                  </span>{" "}
                  is confirmed.
                </div>

                {(paymentMode === "offline" ||
                  paymentRequired === false ||
                  appointment.payment_mode ===
                    "offline") && (
                  <div
                    className="rounded-lg border border-teal-500/20 bg-teal-500/10 p-4 text-left"
                    data-testid="pay-at-reception"
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-teal-400">
                      Payment
                    </div>

                    <div className="mt-2 text-base font-medium text-white">
                      Pay at Reception
                    </div>

                    <div className="mt-1 text-sm text-mb-fg2">
                      Online payment is not required for
                      this appointment. Please pay{" "}
                      <span className="text-white font-medium">
                        {formatCurrency(
                          appointment.consultation_fee ||
                            doctor?.consultation_fee ||
                            0
                        )}
                      </span>{" "}
                      at the hospital reception.
                    </div>
                  </div>
                )}

                <DialogFooter className="!justify-center">
                  <Button
                    onClick={() =>
                      navigate(
                        "/patient/appointments"
                      )
                    }
                    data-testid="success-view-appointments"
                    className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-medium"
                  >
                    View my appointments
                  </Button>
                </DialogFooter>
              </div>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between items-center text-sm border-b border-mb-border pb-2">
      <span className="text-mb-fg2 text-xs uppercase tracking-[0.2em]">
        {k}
      </span>

      <span className="text-white">
        {v}
      </span>
    </div>
  );
}
